'use client';

import { useState } from 'react';
import { HStack } from '@terra/ui-styled-system';
import { Badge } from '../../../components/badge';
import { Button } from '../../../components/button';
import { Field } from '../../../components/field';
import { IconButton } from '../../../components/icon-button';
import { Input } from '../../../components/input';
import { Cancel01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { validateInternalNetblock } from '@terra/shared-utils';

export type InternalNetworkAdvertisedRoutesFieldProps = {
  values: string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
};

export function InternalNetworkAdvertisedRoutesField({
  values,
  onChange,
  disabled = false,
}: InternalNetworkAdvertisedRoutesFieldProps) {
  const t = useTranslations(
    'designSystem.networkSettings.internalConnection.advertisedRoutes',
  );
  const tCommon = useTranslations('designSystem.common');
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const addRoute = () => {
    const result = validateInternalNetblock(draft);

    if (!result.ok) {
      setError(result.message);

      return;
    }

    if (values.includes(result.value)) {
      setError(t('duplicateError'));

      return;
    }

    onChange([...values, result.value]);
    setDraft('');
    setError(null);
  };

  return (
    <Field.Root w="full" invalid={Boolean(error)}>
      <Field.Label>{t('label')}</Field.Label>
      <HStack gap={2} w="full">
        <Input
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            setError(null);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addRoute();
            }
          }}
          placeholder={t('placeholder')}
          disabled={disabled}
        />
        <Button
          size="sm"
          variant="outline"
          onClick={addRoute}
          disabled={disabled || draft.trim().length === 0}
        >
          {tCommon('add')}
        </Button>
      </HStack>
      <Field.HelperText>{t('helper')}</Field.HelperText>
      {error ? <Field.ErrorText>{error}</Field.ErrorText> : null}
      {values.length > 0 ? (
        <HStack gap={2} flexWrap="wrap" pt={2}>
          {values.map((value) => (
            <Badge key={value} variant="outline" size="sm">
              <HStack gap={1} alignItems="center">
                <span>{value}</span>
                <IconButton
                  size="xs"
                  variant="ghost"
                  aria-label={tCommon('delete')}
                  disabled={disabled}
                  onClick={() =>
                    onChange(values.filter((route) => route !== value))
                  }
                >
                  <Cancel01Icon size={12} />
                </IconButton>
              </HStack>
            </Badge>
          ))}
        </HStack>
      ) : null}
    </Field.Root>
  );
}
